import 'chart.js/auto';
import React, { useEffect } from 'react';
import {
  ArcElement, Chart as ChartJS, Legend, Tooltip
} from 'chart.js';
import DOMPurify from 'dompurify';
import { isEmpty } from 'lodash';
import { Doughnut } from 'react-chartjs-2';
import PresentationSVG from 'src/assets/svg/presentation.svg';
import { appUtils, Base } from 'src/components';
import GoalUpdatesFeed from 'src/pagesDashboard/GoalSettings/components/GoalUpdatesFeed';
import MobileViewGoal from 'src/pagesDashboard/GoalSettings/mobile/ViewGoal';
import { useAccount } from 'src/queries/account';
import { useCompany } from 'src/queries/company';
import { useGoal } from 'src/queries/goal';
import CircledIcon from 'src/components/CircledIcon/CircledIcon';
import COMMON_CONSTANTS from 'common/commonConstants';
import commonDateUtils from 'common/commonDateUtils';
import commonQuestions from 'common/commonQuestions';
import commonUtils from 'common/commonUtils';
import STYLE from 'src/constants/style';

const { getGoalStatusValueByKey, getGoalStatusKeyByValue, GOALS } = COMMON_CONSTANTS;

const ViewGoal = ({ goalId }) => {
  const {
    data: goal,
    isFetching: isFetchingGoal,
    isRefetching: isRefetchingGoal,
    isError: isErrorGoal
  } = useGoal(
    goalId,
    {},
    {
      keepPreviousData: true
    }
  );
  const {
    data: assigneeData,
    isFetching: isFetchingGoalAssigneeData,
    isError: isErrorGoalAssigneeData
  } = useAccount(goal.assignee);
  const {
    data: company,
    isFetching: isFetchingCompany,
    isError: isErrorCompany
  } = useCompany();
  const {
    isFetching: isFetchingParentGoal,
    isError: isErrorParentGoal,
    data: parentGoal
  } = useGoal(goal.parentId);
  const {
    data: parentGoalAssigneeData,
    isFetching: isFetchingParentGoalAssigneeData,
    isError: isErrorParentGoalAssigneeData
  } = useAccount(parentGoal.assignee);

  const isInitialFetchingGoal = isFetchingGoal && !isRefetchingGoal;
  const isFetching = isInitialFetchingGoal
    || isFetchingGoalAssigneeData
    || isFetchingCompany
    || isFetchingParentGoal
    || isFetchingParentGoalAssigneeData;
  const isError = isErrorGoal
    || isErrorGoalAssigneeData
    || isErrorCompany
    || isErrorParentGoal
    || isErrorParentGoalAssigneeData;

  const isReady = company && company.id && !isFetching && !isError;

  useEffect(() => {
    const centerLabelPlugin = {
      id: 'centerLabelPlugin',
      beforeDraw: (chart) => {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;

        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;

        const pluginOptions = chart.config.options.plugins.centerLabelPlugin || {};
        const { topLabel = 'Top Label', bottomLabel = 'Bottom Label' } = pluginOptions;

        ctx.save();

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const lines = [topLabel, bottomLabel];

        const topFontSize = 20;
        const bottomFontSize = 40;
        const topLineHeight = topFontSize * 1.2;
        const bottomLineHeight = bottomFontSize * 1.2;

        const totalTextHeight = topLineHeight + bottomLineHeight;
        const startY = centerY - totalTextHeight / 2 + topLineHeight / 2;

        const { family } = ChartJS.defaults.font;
        lines.forEach((line, index) => {
          let lineHeight;

          if (index === 0) {
            ctx.font = `${topFontSize}px ${family}`;
            ctx.fillStyle = '#475467';
            lineHeight = topLineHeight;
          } else {
            ctx.font = `${bottomFontSize}px ${family}`;
            ctx.fillStyle = '#000000';
            lineHeight = bottomLineHeight;
          }

          ctx.fillText(line, centerX, startY + index * lineHeight);
        });

        ctx.restore();
      }
    };

    ChartJS.register(ArcElement, Tooltip, Legend, centerLabelPlugin);

    return () => ChartJS.unregister(centerLabelPlugin);
  }, []);

  if (!isReady) {
    return null;
  }

  const category = goal.categoryId
    ? commonQuestions.getCategory(goal.categoryId, company.questions.CATEGORIES)
    : {
      value: null,
      label: 'No category'
    };

  const assignee = goal.assignee
    ? assigneeData
    : {
      id: null,
      name: 'Unassigned'
    };

  const parentGoalAssignee = parentGoal.assignee
    ? parentGoalAssigneeData
    : {
      id: null,
      name: 'Unassigned'
    };

  const mobileSize = appUtils.getMobileSize();
  const isMobile = mobileSize;

  if (isMobile) {
    return <MobileViewGoal goal={goal} />;
  }

  const loggedUser = appUtils.getLoggedUser();
  const { companyid } = loggedUser;
  const getStatusLabel = (status) => getGoalStatusValueByKey(getGoalStatusKeyByValue(status), companyid);
  const getStatusColor = (status) => {
    let hex = '#727272';

    if (status === GOALS.STATUS.DRAFT) hex = '#727272';
    if (status === GOALS.STATUS.NOT_STARTED) hex = '#5b7fff';
    if (status === GOALS.STATUS.ON_HOLD) hex = '#ff5964';
    if (status === GOALS.STATUS.ON_TRACK) hex = '#56ca00';
    if (status === GOALS.STATUS.OFF_TRACK) hex = '#ffb400';
    if (status === GOALS.STATUS.COMPLETE) hex = '#16b1ff';

    return commonUtils.lightenHex(hex, 0.2);
  };

  const { updates } = goal;
  const labeledData = Object.fromEntries(
    Object.values(GOALS.STATUS).map((status) => [
      getStatusLabel(status),
      {
        count: 0,
        color: '#000000'
      }
    ])
  );

  updates.forEach((update) => {
    const { status } = update;

    const data = labeledData[getStatusLabel(status)];

    data.count += 1;
    data.color = getStatusColor(status);
  });

  const labels = Object.keys(labeledData);
  const data = labels.map((label) => Math.round((labeledData[label].count * 100) / updates.length));
  const backgroundColor = labels.map((label) => labeledData[label].color);

  const graphData = {
    labels,
    datasets: [
      {
        data,
        backgroundColor
      }
    ]
  };

  const graphOptions = {
    aspectRatio: 2 / 1,
    cutout: '85%',
    tooltip: false,
    elements: {
      arc: {
        borderWidth: 0
      }
    },
    plugins: {
      tooltip: {
        enabled: false
      },
      centerLabelPlugin: {
        topLabel: 'Updates',
        bottomLabel: updates.length
      },
      legend: {
        position: 'right',
        onClick: () => {},
        labels: {
          font: {
            size: 20
          },
          padding: 2 * labels.length,
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          generateLabels: (chart) => {
            const { datasets, labels: originalLabels } = chart.data;
            return originalLabels
              .map((label, i) => {
                const value = datasets[0].data[i];

                if (!value) return;

                return {
                  text: `${label} ${value}%`,
                  fillStyle: datasets[0].backgroundColor[i],
                  strokeStyle: 'transparent',
                  hidden: false,
                  index: i
                };
              })
              .filter(Boolean);
          }
        }
      }
    }
  };

  return (
    <>
      <Base classes={`${STYLE.CONTAINER_WHITE} !p-4`} relative>
        <CircledIcon svg={<PresentationSVG className='text-purple' />} />
        <p className='text-xl text-black font-bold mb-0 mr-8'>Goal Tracking</p>
        <p className='m-0 text-sm font-normal mt-2 mb-4 leading-5 text-bluish-gray'>
          Details associated with this specific goal and progress made
        </p>
        <div className='border-b border-mid-gray my-3 transform -translate-x-[16px] w-[calc(100%+32px)]' />
        <div className='w-full flex gap-4'>
          <div className='w-1/2 flex flex-col gap-6'>
            <div className='flex flex-col gap-2'>
              <p className='text-black text-lg font-bold m-0'>Goal Owner</p>
              <p className='text-bluish-gray m-0'>{assignee.name}</p>
            </div>
            <div className='flex flex-col gap-2'>
              <p className='text-black text-lg font-bold m-0'>Goal Title</p>
              <p className='text-bluish-gray m-0'>{goal.title}</p>
            </div>
            <div className='flex flex-col gap-2'>
              <p className='text-black text-lg font-bold m-0'>
                Goal Description
              </p>
              <div
                className='ql-editor text-bluish-gray !p-0'
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(goal.description)
                }}
              />
            </div>
            <div className='flex flex-col gap-2'>
              <p className='text-black text-lg font-bold m-0'>Due Date</p>
              <p className='text-bluish-gray m-0'>
                {commonDateUtils.dateToMonthDayYearFormat(
                  new Date(goal.deadline)
                )}
              </p>
            </div>
          </div>
          <div className='w-1/2 flex flex-col gap-6'>
            {!isEmpty(parentGoal) ? (
              <div className='flex flex-col gap-2'>
                <p className='text-black text-lg font-bold m-0'>Parent Goal</p>
                <p className='text-bluish-gray m-0'>{`${parentGoalAssignee.name} - ${parentGoal.title}`}</p>
              </div>
            ) : null}
            <div className='flex flex-col gap-2'>
              <p className='text-black text-lg font-bold m-0'>Privacy</p>
              <p className='text-bluish-gray m-0'>{category.visibility}</p>
            </div>
            <div className='flex flex-col gap-6'>
              <p className='text-black text-lg font-bold m-0'>Goal Progress</p>
              <div className='h-fit max-h-[250px]'>
                <Doughnut data={graphData} options={graphOptions} />
              </div>
            </div>
          </div>
        </div>
      </Base>
      <GoalUpdatesFeed goal={goal} isLoading={isFetchingGoal} />
    </>
  );
};

export default ViewGoal;
